import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import phone from 'phone';
import { IMAGE_CONSTANT, LOCAL_STORAGE_CONSTANT } from '../../constants/app';
import Button from '../../ui-core/Button/Button';
import Phone from '../../ui-core/Phone';
import { APP_ROUTES, getRoute } from '../../route';
import {
  initiateMobileChangeRequest,
  requestMobileChangeRequest,
  resendCurrentMobileChangeRequest,
  resendNewMobileChangeRequest,
  verifyCurrentMobileChangeRequest,
  verifyNewMobileChangeRequest,
} from '../../api/community';
import { checkForUniqueness } from '../../api/user';
import { doGetUserDataRequest } from '../../store/action/user';
import {
  createNotification,
  NOTIFICATION_TYPE,
} from '../../services/Notification';
import { errorHandler } from '../../utils/helpers';
import { extractJSON } from '../../services/jsonUtil';

import '../NewHome/components/OtpModal/style.css';
import './style.css';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const STEPS = {
  VERIFY_CURRENT: 'verify-current',
  REQUEST: 'request',
  VERIFY_NEW: 'verify-new',
};

const getErrorMessage = error => {
  const handled = errorHandler(error);
  return (
    error?.response?.data?.message ||
    (typeof handled?.message === 'string' ? handled.message : null) ||
    'Something went wrong'
  );
};

const getAuthorizationToken = response =>
  response?.data?.authorization_token ||
  response?.data?.data?.authorization_token;

const maskMobileNumber = (countryCode, mobile) => {
  if (!mobile) return '';
  const dial = countryCode ? `+${String(countryCode).replace(/^\+/, '')}` : '';
  const digits = String(mobile).replace(/\D/g, '');
  if (digits.length <= 4) return `${dial}${digits}`;
  const visible = digits.slice(-4);
  const hiddenCount = Math.max(digits.length - 4, 6);
  return `${dial}${'X'.repeat(hiddenCount)}${visible}`;
};

const ChangePhone = ({ history, user }) => {
  const dispatch = useDispatch();
  const inputRef = useRef([]);
  const phoneFieldRef = useRef(null);
  const hasExistingPhone = Boolean(user?.attributes?.mobile);
  const [step, setStep] = useState(
    hasExistingPhone ? STEPS.VERIFY_CURRENT : STEPS.REQUEST,
  );
  const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(''));
  const [counter, setCounter] = useState(RESEND_SECONDS);
  const [otpError, setOtpError] = useState(false);
  const [authorizationToken, setAuthorizationToken] = useState('');
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneData, setPhoneData] = useState({
    mobile: '',
    country_code: '',
  });
  const [isValidNumber, setIsValidNumber] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const [detectedCountry, setDetectedCountry] = useState('us');

  const currentCountryCode = user?.attributes?.country_code || '';
  const currentMobile = user?.attributes?.mobile || '';
  const maskedCurrentPhone = maskMobileNumber(
    currentCountryCode,
    currentMobile,
  );
  const maskedNewPhone = maskMobileNumber(
    phoneData.country_code,
    phoneData.mobile,
  );
  const phoneCountryIso = detectedCountry;

  const handleBack = state => {
    const url = getRoute(APP_ROUTES.PROFILE_WITH_SUB_ROUTES, {
      pathParam: { section: 'edit-profile' },
    });
    history?.push(url, state);
  };

  const sendCurrentVerificationCode = async () => {
    await initiateMobileChangeRequest();
    setCounter(RESEND_SECONDS);
    setOtp(new Array(OTP_LENGTH).fill(''));
    setOtpError(false);
    inputRef.current[0]?.focus();
  };

  useEffect(() => {
    if (step !== STEPS.VERIFY_CURRENT) return undefined;

    let cancelled = false;

    const init = async () => {
      setIsInitLoading(true);
      try {
        await sendCurrentVerificationCode();
      } catch (error) {
        if (!cancelled) {
          createNotification({
            type: NOTIFICATION_TYPE.ERROR,
            message: getErrorMessage(error),
          });
        }
      } finally {
        if (!cancelled) {
          setIsInitLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [step]);

  useEffect(() => {
    axios
      .get('https://ipapi.co/json/')
      .then(response => {
        const code = response?.data?.country_code;
        if (code) setDetectedCountry(code.toLowerCase());
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // For add-phone flow (no existing phone): initiate the change request on mount
  // so the server creates a session and may return an authorization_token directly.
  useEffect(() => {
    if (hasExistingPhone) return;
    let cancelled = false;
    const init = async () => {
      setIsInitLoading(true);
      try {
        const response = await initiateMobileChangeRequest();
        const token = getAuthorizationToken(response);
        if (!cancelled && token) setAuthorizationToken(token);
      } catch {}
      if (!cancelled) setIsInitLoading(false);
    };
    init();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (
      (step !== STEPS.VERIFY_CURRENT && step !== STEPS.VERIFY_NEW) ||
      counter <= 0
    ) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setCounter(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [counter, step]);

  useEffect(() => {
    if (
      (step === STEPS.VERIFY_CURRENT || step === STEPS.VERIFY_NEW) &&
      !isInitLoading
    ) {
      inputRef.current[0]?.focus();
    }
  }, [step, isInitLoading]);

  useEffect(() => {
    if (step !== STEPS.REQUEST) return;
    phoneFieldRef.current?.querySelector('input')?.focus();
  }, [step]);

  const applyOtpDigits = (digits, startIndex = 0) => {
    const nextOtp = [...otp];

    digits.forEach((digit, offset) => {
      const fieldIndex = startIndex + offset;
      if (fieldIndex < OTP_LENGTH) {
        nextOtp[fieldIndex] = digit;
      }
    });

    setOtp(nextOtp);
    setOtpError(false);

    nextOtp.forEach((digit, fieldIndex) => {
      const input = inputRef.current[fieldIndex];
      if (!input) return;
      input.classList.toggle('filled', Boolean(digit));
      input.classList.remove('error');
    });

    const lastFilledIndex = Math.min(
      startIndex + digits.length - 1,
      OTP_LENGTH - 1,
    );
    inputRef.current[lastFilledIndex]?.focus();
  };

  const handleOtpChange = (element, index) => {
    const raw = element?.value ?? '';
    const digits = raw.replace(/\D/g, '').split('');

    if (digits.length > 1) {
      applyOtpDigits(digits, index);
      return true;
    }

    if (raw && /\D/.test(raw)) return false;

    const nextOtp = otp.map((digit, idx) =>
      idx === index ? digits[0] || '' : digit,
    );

    if (digits[0]) {
      element.classList.add('filled');
    } else {
      element.classList.remove('filled');
    }

    setOtp(nextOtp);
    setOtpError(false);

    if (digits[0]) {
      inputRef.current[index + 1]?.focus();
    }

    return true;
  };

  const handleOtpPaste = (event, index) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH - index);

    if (!pasted) return;

    applyOtpDigits(pasted.split(''), index);
  };

  const handleOtpKeyDown = (event, index, onSubmit) => {
    if (event.keyCode === 8 && !otp[index]) {
      inputRef.current[index - 1]?.focus();
      inputRef.current[index - 1]?.select();
    } else if (event.keyCode === 13 && otp.every(Boolean)) {
      onSubmit();
    }
  };

  const handleResendCurrentCode = async event => {
    event.preventDefault();
    if (counter > 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await resendCurrentMobileChangeRequest();
      setCounter(RESEND_SECONDS);
      setOtp(new Array(OTP_LENGTH).fill(''));
      setOtpError(false);
      inputRef.current[0]?.focus();
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Verification code sent successfully.',
      });
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendNewCode = async event => {
    event.preventDefault();
    if (counter > 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await resendNewMobileChangeRequest();
      setCounter(RESEND_SECONDS);
      setOtp(new Array(OTP_LENGTH).fill(''));
      setOtpError(false);
      inputRef.current[0]?.focus();
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Verification code sent successfully.',
      });
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCurrentSubmit = async () => {
    if (!otp.every(Boolean) || isSubmitting) return;

    setIsSubmitting(true);
    setOtpError(false);

    try {
      const response = await verifyCurrentMobileChangeRequest({
        code: otp.join(''),
      });
      const token = getAuthorizationToken(response);

      if (!token) {
        throw new Error('Unable to verify phone change. Please try again.');
      }

      setAuthorizationToken(token);
      setStep(STEPS.REQUEST);
    } catch (error) {
      setOtpError(true);
      otp.forEach((_, index) => {
        inputRef.current[index]?.classList.add('error');
      });
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkUniqueMobile = async () => {
    try {
      const response = await checkForUniqueness({
        type: 'mobile',
        mobile: phoneData.mobile,
        country_code: phoneData.country_code,
      });
      return response.data;
    } catch (error) {
      return error?.response?.data?.response?.errors[0] || error?.message;
    }
  };

  const handlePhoneOnChange = (phoneNumber, countryCode, countryData) => {
    const validData = phone(
      `${countryCode}${phoneNumber?.replace(/\D+/g, '')}`,
      { country: countryData?.countryCode?.toUpperCase() },
    );
    const mobile = phoneNumber?.replace(/\D+/g, '') || '';

    setPhoneData({
      mobile,
      country_code: countryCode,
    });
    setIsValidNumber(validData.isValid);

    if (phoneNumber[0] === '0') {
      setPhoneError('0 cannot be the first digit of your mobile number.');
    } else if (phoneNumber[0] === '(' && phoneNumber[1] === '0') {
      setPhoneError('0 cannot be the first digit of your mobile number.');
    } else if (!validData.isValid) {
      setPhoneError('Please enter a valid number.');
    } else {
      setPhoneError('');
    }
  };

  const handleRequestSubmit = async event => {
    event.preventDefault();

    if (hasExistingPhone && !authorizationToken) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: 'Please verify your current phone number first.',
      });
      setStep(STEPS.VERIFY_CURRENT);
      return;
    }

    if (!isValidNumber || !phoneData.mobile) return;

    const uniquenessError = await checkUniqueMobile();
    if (uniquenessError) {
      setPhoneError(
        typeof uniquenessError === 'string'
          ? uniquenessError
          : 'This phone number is already in use.',
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await requestMobileChangeRequest({
        authorization_token: authorizationToken,
        mobile: phoneData.mobile,
        country_code: phoneData.country_code,
      });

      setStep(STEPS.VERIFY_NEW);
      setIsInitLoading(false);
      setCounter(RESEND_SECONDS);
      setOtp(new Array(OTP_LENGTH).fill(''));
      setOtpError(false);
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyNewSubmit = async () => {
    if (!otp.every(Boolean) || isSubmitting) return;

    setIsSubmitting(true);
    setOtpError(false);

    try {
      await verifyNewMobileChangeRequest({
        code: otp.join(''),
      });

      const storedUser = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
      if (storedUser?.id) {
        dispatch(doGetUserDataRequest({ userId: storedUser.id }));
      }

      handleBack({ phoneChanged: true });
    } catch (error) {
      setOtpError(true);
      otp.forEach((_, index) => {
        inputRef.current[index]?.classList.add('error');
      });
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCounter = () => {
    const minutes = Math.floor(counter / 60);
    const seconds = counter % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}`;
  };

  const renderOtpStep = ({
    subtitlePhone,
    onSubmit,
    onResend,
    isOtpDisabled,
  }) => (
    <>
      <p className="change-email-subtitle">
        Enter the verification code sent on your Mobile No.{' '}
        <strong>{subtitlePhone}</strong>
      </p>

      <div className="otp-field-wrap change-email-otp-wrap">
        <div className="d-flex justify-content-center">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => {
                inputRef.current[index] = el;
              }}
              className={`otp-field mx-1${otpError ? ' error' : ''}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={isOtpDisabled}
              onChange={e => handleOtpChange(e.target, index)}
              onPaste={e => handleOtpPaste(e, index)}
              onKeyDown={e => handleOtpKeyDown(e, index, onSubmit)}
              onFocus={e => e.target.select()}
            />
          ))}
        </div>
      </div>

      <div className="change-email-resend counter">
        {counter > 0 ? (
          <p>
            Resend code in <span>{formatCounter()}</span>
          </p>
        ) : (
          <button
            type="button"
            className="change-email-resend-btn"
            onClick={onResend}
            disabled={isSubmitting}
          >
            Resend code
          </button>
        )}
      </div>

      <Button
        class="change-password-submit-btn"
        text="SUBMIT"
        type="button"
        onClick={onSubmit}
        isDisabled={isOtpDisabled || !otp.every(Boolean)}
        isLoading={isSubmitting}
      />
    </>
  );

  const renderRequestStep = () => (
    <form
      className="change-password-form change-phone-request-form"
      onSubmit={handleRequestSubmit}
    >
      <div className="change-password-field">
        <label className="account-settings-label" htmlFor="new-phone">
          NEW PHONE NUMBER
        </label>
        <div
          ref={phoneFieldRef}
          className="change-password-input-row change-phone-input-row"
        >
          <Phone
            countryCode={phoneCountryIso}
            containerClass="change-phone-input"
            handleOnChange={handlePhoneOnChange}
          />
        </div>
        {phoneError && (
          <span className="account-settings-error">{phoneError}</span>
        )}
      </div>

      <Button
        class="change-password-submit-btn"
        text="SUBMIT"
        type="submit"
        isDisabled={
          isSubmitting ||
          !isValidNumber ||
          !phoneData.mobile ||
          Boolean(phoneError)
        }
        isLoading={isSubmitting}
      />
    </form>
  );

  const isOtpInputDisabled = isInitLoading || isSubmitting;

  return (
    <div className="groups-page-body change-phone-page">
      <button
        type="button"
        className="change-password-back change-phone-back"
        onClick={() => handleBack()}
      >
        <img
          src={IMAGE_CONSTANT.backIcon}
          alt=""
          className="change-password-back-icon"
        />
        BACK
      </button>

      <div className="change-password-container">
        <h1 className="change-password-title">
          {hasExistingPhone ? 'Change phone number' : 'Add phone number'}
        </h1>

        {step === STEPS.VERIFY_CURRENT &&
          renderOtpStep({
            subtitlePhone: maskedCurrentPhone,
            onSubmit: handleVerifyCurrentSubmit,
            onResend: handleResendCurrentCode,
            isOtpDisabled: isOtpInputDisabled,
          })}

        {step === STEPS.REQUEST && renderRequestStep()}

        {step === STEPS.VERIFY_NEW &&
          renderOtpStep({
            subtitlePhone: maskedNewPhone,
            onSubmit: handleVerifyNewSubmit,
            onResend: handleResendNewCode,
            isOtpDisabled: isOtpInputDisabled,
          })}
      </div>
    </div>
  );
};

ChangePhone.propTypes = {
  history: PropTypes.object,
  user: PropTypes.object,
};

export default ChangePhone;
